// @flow
import moment from 'moment'
import buildSearchLocation from './searchLocation'
import buildDetailedLocation from './detailedLocation'
import buildServiceHours from './serviceHours'
import { buildAmenityItem } from './tripAmenity'
import { config } from '../../../config'

// Types
import type {
  Step,
  CancellationPolicy,
  Amenity,
  TicketType,
} from '../../../models/step'
import type { PrebookSearchStep, SearchLocation } from '../../../api_types'

type PartialResponse = {
  start_location: ?SearchLocation,
  end_location: ?SearchLocation,
  num_passengers: number,
}

/**
 * Return default option from object with options or empty object
 * if no options defined.
 * @param  {Object} options
 * @return {Object}
 */
function getDefaultOption(opts) {
  return (opts && opts.options && opts.options[opts.default_index || 0]) || {}
}

/**
 * Returns a cancellation policy object
 * @param  {Array} policy Policy
 * @return {Array} Formatted policy
 * @private
 */
function buildCancellationPolicy(item): CancellationPolicy {
  return {
    notice: item.notice,
    refundPercent: item.refund_percent,
  }
}

function buildNormalizedPrice(price: *) {
  return {
    compact: config.NO_COMPACT_PRICE ? price.display : price.compact,
    display: price.display,
    value: parseFloat(price.value || '0'),
  }
}

/**
 * Build ticket type object based on step with ticket_types field
 * @param  {Object} step
 * @return {Array<TicketType>}
 */
export function buildTicketTypes(ticketTypes: *): Array<TicketType> {
  if (!ticketTypes) return []

  return ticketTypes.map((x) => {
    const schedules = (x.schedules || []).map((schedule) => ({
      arrivalDatetime: moment.parseZone(schedule.arrival_datetime),
      departureDatetime: moment.parseZone(schedule.departure_datetime),
      duration: {
        value: schedule.duration.value,
      },
      price: buildNormalizedPrice(schedule.price),
      transfers: schedule.transfers,
    }))

    const ticketPrice = x.price ? buildNormalizedPrice(x.price) : null
    const lowestPriceSchedule = schedules.reduce((curr, schedule) => {
      if (!curr) return schedule
      return curr.price.value > schedule.price.value ? schedule : curr
    }, null)

    return {
      isDefault: !!x.default,
      count: x.count || 1,
      label: x.label || '',
      tooltip: x.tooltip || '',
      price: ticketPrice,
      validUntil: moment.parseZone(x.valid_until),
      validUntilForHumans: moment.parseZone(x.valid_until_for_humans),
      defaultScheduleId: x.default_schedule_index,
      ticketId: x.code || x.ticket_id || '',
      ticketName: x.display_name || x.ticket_name || '',
      validity: x.validity,
      lowestPrice: lowestPriceSchedule?.price || ticketPrice,
      schedules,
    }
  })
}

/**
 * By given direction type return step field names to get propper
 * location from step/response.
 * @param  {string} type
 * @return {Object}
 */
function getLocationFieldNames(type: 'from' | 'to') {
  const diffFieldName = type === 'from' ? 'start_difference' : 'end_difference'
  const locationFieldName = type === 'from' ? 'start_location' : 'end_location'
  const stationFieldName =
    type === 'from' ? 'pickup_station' : 'dropoff_station'

  return { diffFieldName, locationFieldName, stationFieldName }
}

/**
 * Build step location object from all the parts available
 * @param  {PrebookSearchStep} step
 * @param  {SearchResponse} response
 * @param  {string} type
 * @param  {boolean} isReturnStep
 * @return {Object}
 */
function buildStepLocation(step, response, type: 'from' | 'to', isReturnStep) {
  const offsets = step.details.location_offsets || {}
  const directFields = getLocationFieldNames(type)
  const reverseFields = getLocationFieldNames(type === 'from' ? 'to' : 'from')

  const rawStation = step.details[directFields.stationFieldName]
  const distance = offsets[directFields.diffFieldName] || 0
  const rawLocation = isReturnStep
    ? response[reverseFields.locationFieldName]
    : response[directFields.locationFieldName]

  return {
    location: !rawLocation ? null : buildSearchLocation(rawLocation),
    distance,
    station: !rawStation ? null : buildDetailedLocation(rawStation),
  }
}

/**
 * Build step for prebook trip
 * @param  {Object} step     Step object of the trip
 * @param  {Object} trip     The trip object
 * @param  {Object} response The whole search response object
 * @return {Object}
 * @private
 */
function buildPrebookStep(
  step: PrebookSearchStep,
  response: PartialResponse,
  isReturnStep: boolean
): Step {
  const amenities = step.details.amenities
    .map(buildAmenityItem)
    .filter(config.AMENITIES_FILTER)
  const waitTime = step.details.wait_time || {}
  const cancellation = step.details.cancellation || {}
  const serviceHours = step.details.service_hours || {}
  const startLocation = buildStepLocation(step, response, 'from', isReturnStep)
  const endLocation = buildStepLocation(step, response, 'to', isReturnStep)
  const alternativeTimes = step.details.alternative_times || {
    options: [],
    default_index: 0,
  }
  const alternativeDepartureDatetime = getDefaultOption(alternativeTimes)
    .departure_datetime
  const alternativeArrivalDatetime = getDefaultOption(alternativeTimes)
    .arrival_datetime
  const departureDatetime =
    alternativeDepartureDatetime || step.details.departure_datetime
  const arrivalDatetime =
    alternativeArrivalDatetime ||
    step.details.arrival_datetime ||
    departureDatetime

  const meetAndGreet = amenities.find(
    (x: Amenity) => x.id === 'meet_and_greet'
  ) || {
    price: {},
    included: false,
  }

  return {
    from: startLocation,
    to: endLocation,
    description: step.details.description || '',
    main: step.main,
    alternativeTimesIndex: alternativeTimes.default_index,
    alternativeTimes: alternativeTimes.options.map((x) => x.departure_datetime),
    serviceHours: buildServiceHours(step.details.service_hours),
    ticketTypes: buildTicketTypes(step.details.ticket_types),
    passTypes: buildTicketTypes(step.details.pass_types),
    arrivalTimes: alternativeTimes.options.map((x) => x.arrival_datetime),
    arrivalDatetime: moment.parseZone(arrivalDatetime),
    departureDatetime: moment.parseZone(departureDatetime),
    type: step.step_type,
    lineName: step.details.line_name || '',
    lineColor: step.details.line_color || '',
    stops: step.details.stops || 0,
    bookingDetails: {
      gratuityIncluded: !!step.details.price.gratuity_included,
      tollsIncluded: !!step.details.price.tolls_included,
      meetAndGreetDefault: meetAndGreet.included,
      meetAndGreetAvailable: !!meetAndGreet.price.total,
      meetAndGreetPrice: meetAndGreet.price.total,
      meetAndGreetRawPrice: parseFloat(meetAndGreet.price.raw || '0'),
      cancellationPolicy: (cancellation.policy || []).map(
        buildCancellationPolicy
      ),
      cancellableOffline: !!cancellation.cancellable_offline,
      cancellableOnline: !!cancellation.cancellable_online,
      amendable: !!cancellation.amendable,
      waitTime: waitTime.minutes_included || 0,
      extraPaxRequired: !!step.details.extra_pax_required,
      acceptsGratuity: !!step.details.price.gratuity_accepted,
      showFlightInfo: !!step.details.flight_info_required,
      areTermsInternal: step.details.are_terms_internal,
      termsOfServiceURL: step.details.terms_url || '',
      notes: step.details.notes || '',
      isBookable: step.details.bookable,
      startAddressIncomplete: !!step.details.start_address_incomplete,
      endAddressIncomplete: !!step.details.end_address_incomplete,
      startTime: serviceHours.start_time,
      endTime: serviceHours.end_time,
      showTollsAndGratuity: !step.details.hide_options,
      specialInstructionsPlaceholder:
        step.details.special_instructions_placeholder,
      hourlyDetails: step.details.hourly_details
        ? {
            includedKilometersPerHour:
              step.details.hourly_details.included_kilometers_per_hour,
            totalIncludedKilometers:
              step.details.hourly_details.total_included_kilometers,
            originalRequested: step.details.hourly_details.original_requested,
            minimum: step.details.hourly_details.minimum,
            hoursRequested: step.details.hourly_details.hours_requested,
          }
        : null,
    },
    vehicle: {
      type: {
        id: step.details.vehicle.vehicle_type.key, // FE should use this id
        key: step.details.vehicle.vehicle_type.name, // And this for compatibility
        name: step.details.vehicle.vehicle_type.name,
      },
      maxPassengers: step.details.vehicle.max_passengers,
      maxBags: step.details.vehicle.max_bags,
      isMaxBagsPerPerson: step.details.vehicle.is_max_bags_per_person,
      imageUrl:
        step.details.vehicle.image || step.details.provider.logo_url || '',
      numVehicles: step.details.vehicle.num_vehicles || 1,
      totalBags: step.details.vehicle.total_bags,
      make: step.details.vehicle.make || '',
      model: step.details.vehicle.model || '',
      vehicleClass:
        step.details.vehicle.vehicle_class_detail?.vehicle_class_id || null, // eslint-disable-line camelcase
      vehicleClassName:
        step.details.vehicle.vehicle_class_detail?.display_name || '', // eslint-disable-line camelcase
    },
    provider: {
      email: '',
      phoneNumber: '',
      logoUrl:
        step.details.provider.logo_url || step.details.vehicle.image || '',
      name: step.details.provider.display_name || step.details.provider.name,
      realName:
        step.details.provider.name || step.details.provider.display_name,
      rating: parseInt(step.details.provider.rating, 10),
      ratingFloat: parseFloat(step.details.provider.rating_with_decimals),
      ratingCount: step.details.provider.rating_count,
      supplierScore: step.details.provider.supplier_score
        ? parseFloat(step.details.provider.supplier_score)
        : null,
    },
    amenities,
    passengersCount: response.num_passengers,
    accurateTime: step.details.time || 0,
  }
}

export default buildPrebookStep
